import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Icon } from '@/ds';
import { EmptyState } from '@/components/EmptyState';
import { Money, Provenance } from '@/components/money';
import { useServices, type ServiceSummary } from '@/api/hooks';
import { formatDate } from '@/lib/format';
import { useIsMobile } from '@/lib/useMediaQuery';
import { ServiceFormDialog } from './services/ServiceDialogs';
import { ExpensesCard } from './finance/ExpensesCard';
import { QueryState } from '@/components/QueryState';

export const BILLING_LABELS: Record<string, string> = {
  prepaid_balance: 'Предоплата',
  postpaid_invoice: 'Постоплата',
  subscription: 'Подписка',
};

export const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  digitalocean: 'DigitalOcean',
  google_workspace: 'Google Workspace',
  manual: 'Вручную',
};

/** Прогноз словами. Если расхода не было, честно пишем это вместо числа. */
export function forecastText(service: ServiceSummary): string {
  if (service.billingModel !== 'prepaid_balance') return '—';
  const forecast = service.forecast;
  if (!forecast) return '—';
  if ((service.balanceMinor ?? 0) < 0) return 'баланс отрицательный, нужна сверка';
  if (forecast.days === null) return `нет расхода за ${forecast.windowDays} дней`;
  const suffix = forecast.isApproximate ? ', приблизительно' : '';
  return `хватит на ${forecast.days} дн.${suffix}`;
}

function balanceTone(service: ServiceSummary): 'normal' | 'danger' {
  return (service.balanceMinor ?? 0) < 0 ? 'danger' : 'normal';
}

function ServiceRow({ service, onOpen }: { service: ServiceSummary; onOpen: () => void }) {
  return (
    <tr
      className="kj-row-link"
      role="link"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <td data-label="Сервис" style={{ color: 'var(--text-strong)', fontWeight: 500 }}>
        {service.name}
        {service.isActive ? null : (
          <span style={{ marginLeft: 8 }}>
            <Badge tone="neutral">Отключён</Badge>
          </span>
        )}
        <div style={{ font: '400 12px var(--font-text)', color: 'var(--text-subtle)' }}>
          {PROVIDER_LABELS[service.provider] ?? service.provider}
        </div>
      </td>
      <td data-label="Биллинг">{BILLING_LABELS[service.billingModel] ?? service.billingModel}</td>
      <td data-label="Баланс">
        {service.billingModel === 'prepaid_balance' ? (
          <>
            <Money
              minor={service.balanceMinor}
              currency={service.currency}
              size="sm"
              tone={balanceTone(service)}
            />
            <div style={{ font: '400 11px var(--font-text)', color: 'var(--text-subtle)' }}>
              расчётный
            </div>
          </>
        ) : service.billingModel === 'subscription' && service.subscription ? (
          <>
            <Money minor={service.subscription.amount} currency={service.currency} size="sm" />
            <div style={{ font: '400 11px var(--font-text)', color: 'var(--text-subtle)' }}>
              списание {formatDate(service.subscription.nextChargeAt)}
            </div>
          </>
        ) : (
          <span style={{ color: 'var(--text-subtle)' }}>—</span>
        )}
      </td>
      <td data-label="Прогноз" style={{ color: 'var(--text-muted)' }}>{forecastText(service)}</td>
      <td data-label="Данные" style={{ color: 'var(--text-subtle)' }}>
        {service.syncStatus === 'never'
          ? 'вручную'
          : service.syncStatus === 'ok'
            ? 'обновлено'
            : 'ошибка'}
      </td>
    </tr>
  );
}

function ServiceCardMobile({ service, onOpen }: { service: ServiceSummary; onOpen: () => void }) {
  return (
    <div
      onClick={onOpen}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{ borderRadius: 'var(--radius-md)' }}
    >
    <Card padding="sm" interactive>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ font: '500 15px var(--font-text)', color: 'var(--text-strong)' }}>
            {service.name}
          </div>
          <Provenance>
            {(PROVIDER_LABELS[service.provider] ?? service.provider) +
              ' · ' +
              (BILLING_LABELS[service.billingModel] ?? service.billingModel)}
          </Provenance>
        </div>
        {service.billingModel === 'prepaid_balance' ? (
          <Money
            minor={service.balanceMinor}
            currency={service.currency}
            tone={balanceTone(service)}
          />
        ) : service.subscription ? (
          <Money minor={service.subscription.amount} currency={service.currency} />
        ) : null}
      </div>
      <div style={{ marginTop: 'var(--space-3)' }}>
        <Provenance>{forecastText(service)}</Provenance>
      </div>
    </Card>
    </div>
  );
}

export function FinanceScreen() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const services = useServices();
  const [creating, setCreating] = useState(false);

  const rows = services.data?.services ?? [];

  if (services.isLoading || services.isError) {
    return (
      <div className="kj-page">
        <QueryState query={services} what="сервисы" />
      </div>
    );
  }

  return (
    <div className="kj-page">
      {rows.length === 0 ? (
        <EmptyState
          icon="Wallet"
          title="Сервисов пока нет"
          description="Добавьте первый сервис, чтобы видеть баланс, расход и прогноз. У OpenAI, Anthropic и DigitalOcean расход собирается сам после ввода ключа, остальное вносится руками."
          action={
            <Button onClick={() => setCreating(true)} iconLeft={<Icon name="Plus" size={16} />}>
              Создать сервис
            </Button>
          }
        />
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="secondary"
              onClick={() => setCreating(true)}
              iconLeft={<Icon name="Plus" size={16} />}
            >
              Создать сервис
            </Button>
          </div>

          {isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              {rows.map((service) => (
                <ServiceCardMobile
                  key={service.id}
                  service={service}
                  onOpen={() => navigate(`/finance/${service.id}`)}
                />
              ))}
            </div>
          ) : (
            <Card padding="md">
              <div className="kj-scroll-x">
                <table className="kj-table">
                  <thead>
                    <tr>
                      <th>Сервис</th>
                      <th>Биллинг</th>
                      <th>Баланс</th>
                      <th>Прогноз</th>
                      <th>Данные</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((service) => (
                      <ServiceRow
                        key={service.id}
                        service={service}
                        onOpen={() => navigate(`/finance/${service.id}`)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          <Provenance>
            Расход по дням и разбивка по моделям — в карточке сервиса. Общий график трат
            и сравнение с прошлым месяцем — на обзоре.
          </Provenance>
        </>
      )}

      <ExpensesCard />

      <ServiceFormDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}
